import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Select, MenuItem,
    FormControl, InputLabel, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogActions, DialogContent,
    DialogTitle, Chip, Snackbar, Alert, CircularProgress, Tooltip,
    InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PolicyIcon from '@mui/icons-material/Policy';
import ArticleIcon from '@mui/icons-material/Article'; // <-- ÍCONO AÑADIDO

// Interfaz para un objeto de Landing Page
interface LandingPage {
    id: number;
    name: string;
    url: string;
    status: 'active' | 'inactive';
    created_at: string;
    updated_at: string;
    // Se añade health_status a LandingPage para que la tabla y la API lo puedan usar.
    health_status: string | null; 
}

// Interfaz para el resultado de la verificación de salud (igual que en DomainsPage)
interface HealthCheckResult {
    status: 'clean' | 'banned' | 'suspicious';
    summary: string;
    details: {
        google: { threats: string[] };
        virustotal: { malicious: number; suspicious: number };
    };
}


// --- Helper Components ---

/**
 * LandingPageForm Component
 * Formulario para crear y editar Landing Pages.
 * Se han añadido validaciones de URL y manejo de estado de envío.
 */
const LandingPageForm: React.FC<{
    newName: string;
    setNewName: React.Dispatch<React.SetStateAction<string>>;
    newUrl: string;
    setNewUrl: React.Dispatch<React.SetStateAction<string>>;
    newStatus: 'active' | 'inactive';
    setNewStatus: React.Dispatch<React.SetStateAction<'active' | 'inactive'>>;
    handleSubmit: (event: React.FormEvent) => void;
    editingPageId: number | null;
    cancelEditing: () => void;
    formError: string | null;
    isSubmitting: boolean; // Prop para el estado de envío del formulario
}> = ({ newName, setNewName, newUrl, setNewUrl, newStatus, setNewStatus, handleSubmit, editingPageId, cancelEditing, formError, isSubmitting }) => {

    // Función para validar el formato de una URL completa (con http/https)
    const isValidUrl = (url: string) => {
        try {
            new URL(url); // Intenta crear un objeto URL. Si falla, no es una URL válida.
            return true;
        } catch (e) {
            return false;
        }
    };

    const isNameEmpty = !newName.trim();
    const isUrlEmpty = !newUrl.trim();
    const urlFormatError = newUrl.trim() && !isValidUrl(newUrl.trim()) ? "Formato de URL inválido (ej. https://tulandingpage.com)" : "";

    return (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
            <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                {editingPageId ? 'Editar Landing Page' : 'Crear Nueva Landing Page'}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={3}>
                    <TextField
                        name="lp_name"
                        label="Nombre de la Landing Page"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Nombre de la Landing Page"
                        required
                        fullWidth
                        variant="outlined"
                        error={isNameEmpty && !!formError}
                        helperText={isNameEmpty && !!formError ? "El nombre es obligatorio." : ""}
                    />
                    <TextField
                        name="lp_url"
                        label="URL"
                        type="url"
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        placeholder="https://tulandingpage.com/prelander.html"
                        required
                        fullWidth
                        variant="outlined"
                        error={isUrlEmpty && !!formError || !!urlFormatError}
                        helperText={(isUrlEmpty && "La URL es obligatoria.") || urlFormatError || formError}
                        // Se añade el botón de previsualización al campo de URL
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Previsualizar URL">
                                        <IconButton
                                            component="a"
                                            href={isValidUrl(newUrl) ? newUrl : '#'} // Solo hace click si la URL es válida
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            disabled={!isValidUrl(newUrl)} // Deshabilitar si la URL no es válida
                                            edge="end"
                                            color="primary"
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Estado</InputLabel>
                        <Select
                            name="lp_status"
                            value={newStatus}
                            label="Estado"
                            onChange={(e) => setNewStatus(e.target.value as 'active' | 'inactive')}
                        >
                            <MenuItem value="active">Activa</MenuItem>
                            <MenuItem value="inactive">Inactiva</MenuItem>
                        </Select>
                    </FormControl>
                    <Stack direction="row" spacing={2}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || isNameEmpty || isUrlEmpty || !!urlFormatError} // Deshabilitar si está enviando o hay errores
                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddCircleOutlineIcon />}
                        >
                            {editingPageId ? 'Actualizar Landing Page' : 'Crear Landing Page'}
                        </Button>
                        {editingPageId && (
                            <Button type="button" variant="text" onClick={cancelEditing} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Box>
        </Paper>
    );
};

/**
 * Component to display the domain health status as a Chip.
 * (Re-used from DomainsPage for consistency in health check dialog)
 */
const HealthStatusChip: React.FC<{ status: string | null }> = ({ status }) => {
    const statusMap: { [key: string]: { label: string; color: 'success' | 'error' | 'warning' | 'default' } } = {
        clean: { label: 'Limpio', color: 'success' },
        banned: { label: 'Baneado', color: 'error' },
        suspicious: { label: 'Sospechoso', color: 'warning' },
    };

    if (status === null) {
        return <Chip label="No revisado" color="default" size="small" variant="outlined" />;
    }

    if (statusMap[status]) {
        const currentStatus = statusMap[status];
        return <Chip label={currentStatus.label} color={currentStatus.color} size="small" variant="outlined" />;
    }

    return <Chip label={status} color="info" size="small" variant="outlined" />;
};


/**
 * LandingPagesTable Component
 * Tabla para mostrar las Landing Pages registradas.
 */
const LandingPagesTable: React.FC<{
    landingPages: LandingPage[];
    startEditing: (page: LandingPage) => void;
    onDeleteConfirm: (id: number) => void;
    onCheckHealth: (page: LandingPage) => void; // Añadir prop para la función de revisión de salud
    checkingHealthId: number | null; // Añadir prop para el ID del elemento en revisión
}> = ({ landingPages, startEditing, onDeleteConfirm, onCheckHealth, checkingHealthId }) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    if (landingPages.length === 0) {
        return <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No hay Landing Pages registradas.</Typography>;
    }

    return (
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px' }}>
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>ID</TableCell>
                            <TableCell sx={headerCellStyle}>Nombre</TableCell>
                            <TableCell sx={headerCellStyle}>URL</TableCell>
                            <TableCell sx={headerCellStyle}>Estado</TableCell>
                            {/* Re-añadimos la columna de Salud para mostrar el estado de la LP */}
                            <TableCell sx={headerCellStyle}>Salud URL</TableCell>
                            <TableCell sx={headerCellStyle}>Creado</TableCell>
                            <TableCell sx={headerCellStyle}>Actualizado</TableCell>
                            <TableCell align="right" sx={headerCellStyle}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {landingPages.map((page) => (
                            <TableRow key={page.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{page.id}</TableCell>
                                <TableCell sx={{ textTransform: 'none' }}>{page.name}</TableCell>
                                <TableCell>
                                    <Tooltip title={page.url}>
                                        <Typography
                                            component="a"
                                            href={page.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                textDecoration: 'none',
                                                wordBreak: 'break-all',
                                                display: 'block',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '200px',
                                            }}
                                        >
                                            {page.url}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={page.status === 'active' ? 'Activa' : 'Inactiva'}
                                        color={page.status === 'active' ? 'success' : 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                {/* Celda para mostrar el estado de salud de la URL */}
                                <TableCell>
                                    {checkingHealthId === page.id ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <HealthStatusChip status={page.health_status} />
                                    )}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(page.created_at).toLocaleString()}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(page.updated_at).toLocaleString()}</TableCell>
                                <TableCell align="right">
                                    {/* Botón de revisión de salud */}
                                    <Tooltip title="Revisar Salud URL">
                                        <IconButton size="small" onClick={() => onCheckHealth(page)} color="info" disabled={checkingHealthId === page.id}>
                                            <PolicyIcon />
                                        </IconButton>
                                    </Tooltip>
                                    {/* Botón de previsualización */}
                                    <Tooltip title="Previsualizar URL">
                                        <IconButton component="a" href={page.url} target="_blank" rel="noopener noreferrer" size="small" color="primary">
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar Landing Page">
                                        <IconButton size="small" onClick={() => startEditing(page)} color="primary">
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar Landing Page">
                                        <IconButton size="small" onClick={() => onDeleteConfirm(page.id)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

/**
 * ConfirmationDialog Component
 * Diálogo genérico para confirmaciones (ej. eliminación).
 */
const ConfirmationDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    children: React.ReactNode;
}> = ({ open, onClose, onConfirm, title, children }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent><Typography color="text.secondary">{children}</Typography></DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={onConfirm} color="error" variant="contained">Confirmar Eliminación</Button>
        </DialogActions>
    </Dialog>
);

/**
 * HealthCheckDialog Component
 * Diálogo para mostrar los resultados de la verificación de salud de una URL.
 * (Re-utilizado de DomainsPage)
 */
const HealthCheckDialog: React.FC<{ open: boolean; onClose: () => void; result: HealthCheckResult | null }> = ({ open, onClose, result }) => {
    if (!result) return null; // No renderizar si no hay resultados

    const { status, summary, details } = result;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle color="text.primary">Resultado de la Verificación de Salud</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <HealthStatusChip status={status} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} color="text.secondary">{summary}</Typography>

                    <Box>
                        <Typography variant="h6" gutterBottom color="text.primary">Google Safe Browse</Typography>
                        {details.google.threats.length > 0 ? (
                            <Chip label={details.google.threats.join(', ')} color="error" />
                        ) : (
                            <Typography color="text.secondary">No se encontraron amenazas.</Typography>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="h6" gutterBottom color="text.primary">VirusTotal</Typography>
                        <Stack direction="row" spacing={2}>
                            <Chip label={`${details.virustotal.malicious} Maliciosos`} color={details.virustotal.malicious > 0 ? 'error' : 'default'} />
                            <Chip label={`${details.virustotal.suspicious} Sospechosos`} color={details.virustotal.suspicious > 0 ? 'warning' : 'default'} />
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};


// --- MAIN PAGE COMPONENT ---
const LandingPagesPage: React.FC = () => {
    // --- ESTADOS ---
    const [landingPages, setLandingPages] = useState<LandingPage[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // Nuevo estado para controlar el envío del formulario
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    const [newName, setNewName] = useState<string>('');
    const [newUrl, setNewUrl] = useState<string>('');
    const [newStatus, setNewStatus] = useState<'active' | 'inactive'>('active');
    const [editingPageId, setEditingPageId] = useState<number | null>(null);

    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean, id: number | null }>({ open: false, id: null });
    const [searchTerm, setSearchTerm] = useState('');

    // Estados para la funcionalidad de revisión de salud
    const [checkingHealthId, setCheckingHealthId] = useState<number | null>(null);
    const [healthResult, setHealthResult] = useState<HealthCheckResult | null>(null);
    const [isHealthDialogOpen, setIsHealthDialogOpen] = useState(false);

    /**
     * Función para obtener las Landing Pages de la API.
     * Usa useCallback para memorizar la función.
     */
    const fetchLandingPages = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/landing-pages`);
            setLandingPages(response.data);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Error al obtener Landing Pages.');
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al cargar Landing Pages.'});
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Efecto para cargar las Landing Pages al montar el componente.
     */
    useEffect(() => {
        fetchLandingPages();
    }, [fetchLandingPages]);

    /**
     * Manejador para la creación o actualización de Landing Pages.
     * Se ha añadido el manejo del estado 'isSubmitting'.
     */
    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setNotification(null);
        setIsSubmitting(true); // Inicia el estado de envío

        // Validación de campos obligatorios
        if (!newName.trim() || !newUrl.trim()) {
            setNotification({ type: 'error', message: 'Nombre y URL son campos obligatorios.' });
            setIsSubmitting(false);
            return;
        }

        // Validación de formato de URL
        try {
            new URL(newUrl.trim());
        } catch (e) {
            setNotification({ type: 'error', message: 'La URL no tiene un formato válido.' });
            setIsSubmitting(false);
            return;
        }

        const landingPageData = {
            name: newName,
            url: newUrl,
            status: newStatus,
        };

        const endpoint = editingPageId ? `/landing-pages/${editingPageId}` : '/landing-pages';
        const method = editingPageId ? 'PUT' : 'POST';

        try {
            await axios({ method, url: `${API_BASE_URL}${endpoint}`, data: landingPageData });
            setNewName('');
            setNewUrl('');
            setNewStatus('active');
            setEditingPageId(null);
            setNotification({ type: 'success', message: `Landing Page ${editingPageId ? 'actualizada' : 'creada'} con éxito!` });
            fetchLandingPages(); // Recarga las landing pages después de la operación
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error desconocido al guardar.' });
        } finally {
            setIsSubmitting(false); // Finaliza el estado de envío
        }
    };

    /**
     * Manejador para confirmar la eliminación de una Landing Page.
     */
    const handleDeleteConfirm = (id: number) => {
        setDeleteDialog({ open: true, id });
    };

    /**
     * Manejador para ejecutar la eliminación de una Landing Page.
     */
    const handleDelete = useCallback(async () => {
        if (!deleteDialog.id) return;
        try {
            await axios.delete(`${API_BASE_URL}/landing-pages/${deleteDialog.id}`);
            setNotification({ type: 'success', message: `Landing Page (ID: ${deleteDialog.id}) eliminada con éxito!` });
            fetchLandingPages();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error desconocido al eliminar.' });
        } finally {
            setDeleteDialog({ open: false, id: null });
        }
    }, [deleteDialog.id, fetchLandingPages]);

    /**
     * Inicia el modo de edición para una Landing Page.
     */
    const startEditing = useCallback((page: LandingPage) => {
        setEditingPageId(page.id);
        setNewName(page.name);
        setNewUrl(page.url);
        setNewStatus(page.status);
        setNotification(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    /**
     * Cancela el modo de edición y limpia el formulario.
     */
    const cancelEditing = useCallback(() => {
        setEditingPageId(null);
        setNewName('');
        setNewUrl('');
        setNewStatus('active');
        setNotification(null);
    }, []);

    /**
     * Revisa la salud de la URL de una Landing Page utilizando la API.
     */
    const handleCheckHealth = useCallback(async (page: LandingPage) => {
        setCheckingHealthId(page.id);
        setNotification(null);
        try {
            const response = await axios.post(`${API_BASE_URL}/health/check-domain`, {
                urlToCheck: page.url, // Usamos la URL de la Landing Page
                entityId: page.id,
                entityType: 'landing_page' // Especificamos el tipo de entidad
            });
            setHealthResult(response.data);
            setIsHealthDialogOpen(true);
            fetchLandingPages(); // Recargar para actualizar el health_status en la tabla
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al revisar la salud de la URL.' });
        } finally {
            setCheckingHealthId(null);
        }
    }, [fetchLandingPages]);

    /**
     * Filtra las Landing Pages basándose en el término de búsqueda.
     * Usa useMemo para optimizar el rendimiento.
     */
    const filteredLandingPages = useMemo(() => {
        if (!searchTerm) return landingPages;
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return landingPages.filter(page =>
            String(page.id).includes(lowerCaseSearchTerm) ||
            page.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            page.url.toLowerCase().includes(lowerCaseSearchTerm) ||
            page.status.toLowerCase().includes(lowerCaseSearchTerm) ||
            // Incluir el health_status en la búsqueda
            String(page.health_status).toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [landingPages, searchTerm]);

    // Renderizado condicional para estados de carga y error inicial
    if (isLoading) return (
        <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary" sx={{ ml: 2 }}>Cargando Landing Pages...</Typography>
        </Container>
    );

    if (error && landingPages.length === 0) return <Alert severity="error" sx={{ m: 3 }}>Error al cargar Landing Pages: {error}</Alert>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4, fontFamily: 'Inter, sans-serif' }}>
            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>

            <ConfirmationDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, id: null })}
                onConfirm={handleDelete}
                title="Confirmar Eliminación de Landing Page"
            >
                ¿Estás seguro de que quieres eliminar esta Landing Page? Esta acción es irreversible y podría afectar campañas asociadas.
            </ConfirmationDialog>

            {/* Diálogo para mostrar los resultados de la verificación de salud */}
            <HealthCheckDialog
                open={isHealthDialogOpen}
                onClose={() => setIsHealthDialogOpen(false)}
                result={healthResult}
            />

            {/* --- CAMBIO: Título actualizado --- */}
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <ArticleIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Gestión de Landing Pages
            </Typography>

            <LandingPageForm
                newName={newName}
                setNewName={setNewName}
                newUrl={newUrl}
                setNewUrl={setNewUrl}
                newStatus={newStatus}
                setNewStatus={setNewStatus}
                handleSubmit={handleSubmit}
                editingPageId={editingPageId}
                cancelEditing={cancelEditing}
                formError={notification?.type === 'error' ? notification.message : null}
                isSubmitting={isSubmitting} // Pasar el estado de envío al formulario
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 5, mb: 2 }}>
                <Typography variant="h5" component="h2" color="text.primary" sx={{ fontWeight: 'bold' }}>Landing Pages Registradas</Typography>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Buscar por ID, Nombre, URL o Estado..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                    sx={{ minWidth: { xs: '100%', sm: '250px' } }}
                />
            </Stack>

            <LandingPagesTable
                landingPages={filteredLandingPages}
                startEditing={startEditing}
                onDeleteConfirm={handleDeleteConfirm}
                onCheckHealth={handleCheckHealth}
                checkingHealthId={checkingHealthId}
            />
        </Container>
    );
};

export default LandingPagesPage;